import type { NarratingSubagentKind, OntologyAgentEvent } from '@atbash-labs/ontology-agent-wire'

/** Stable pill key for ingest live CoT: lead, or one pill per dispatch. */
export type IngestCotPillKey = 'lead' | `dispatch:${string}`

/** Lead pill key — shared by the CoT reducer and the conversation pill strip. */
export const INGEST_LEAD_PILL_KEY: IngestCotPillKey = 'lead'

export interface IngestCotCommittedTurn {
  text: string
  /**
   * ISO timestamp from the progress-commit SSE (`event.timestamp` — server
   * clock at tool_use). Used to interleave held rows with Q&A in the
   * conversation timeline until the durable poll replaces them.
   */
  at: string
}

export interface IngestCotPillTail {
  liveText: string
  committed: readonly IngestCotCommittedTurn[]
  subagentKind: NarratingSubagentKind
  dispatchId?: string
  sectionId?: string
  agentRunId?: string
}

export type IngestCotByPill = Readonly<Record<string, IngestCotPillTail>>

/** Derive the pill key from a progress event's attribution. */
export function ingestCotPillKey(event: { dispatchId?: string }): IngestCotPillKey {
  return event.dispatchId ? `dispatch:${event.dispatchId}` : INGEST_LEAD_PILL_KEY
}

export interface IngestCotReduceState {
  byPill: Record<string, IngestCotPillTail>
  /** In-flight turn text keyed by pill (not yet committed). */
  turnTextByPill: Record<string, string>
}

export function emptyIngestCotReduceState(): IngestCotReduceState {
  return { byPill: {}, turnTextByPill: {} }
}

function ensurePill(
  state: IngestCotReduceState,
  key: string,
  meta: {
    subagentKind: NarratingSubagentKind
    dispatchId?: string
    sectionId?: string
    agentRunId?: string
  }
): IngestCotPillTail {
  const existing = state.byPill[key]
  if (existing) {
    return {
      ...existing,
      ...(meta.dispatchId ? { dispatchId: meta.dispatchId } : {}),
      ...(meta.sectionId ? { sectionId: meta.sectionId } : {}),
      ...(meta.agentRunId ? { agentRunId: meta.agentRunId } : {}),
      subagentKind: meta.subagentKind
    }
  }
  return {
    liveText: '',
    committed: [],
    subagentKind: meta.subagentKind,
    ...(meta.dispatchId ? { dispatchId: meta.dispatchId } : {}),
    ...(meta.sectionId ? { sectionId: meta.sectionId } : {}),
    ...(meta.agentRunId ? { agentRunId: meta.agentRunId } : {})
  }
}

/**
 * Pure reducer for ingest progress SSE events → per-pill live CoT tails.
 * Returns the next state (immutably) or the same reference when the event is
 * irrelevant.
 */
export function reduceIngestCotEvent(
  state: IngestCotReduceState,
  event: OntologyAgentEvent
): IngestCotReduceState {
  if (
    event.type !== 'agent_orchestrator_subagent_progress_delta' &&
    event.type !== 'agent_orchestrator_subagent_progress_commit' &&
    event.type !== 'agent_orchestrator_subagent_progress_discard'
  ) {
    return state
  }

  const key = ingestCotPillKey(event)
  const meta = {
    subagentKind: event.subagentKind,
    ...(event.dispatchId ? { dispatchId: event.dispatchId } : {}),
    ...(event.sectionId ? { sectionId: event.sectionId } : {}),
    ...(event.agentRunId ? { agentRunId: event.agentRunId } : {})
  }
  const pill = ensurePill(state, key, meta)

  if (event.type === 'agent_orchestrator_subagent_progress_delta') {
    const turnText = (state.turnTextByPill[key] ?? '') + event.textDelta
    return {
      turnTextByPill: { ...state.turnTextByPill, [key]: turnText },
      byPill: {
        ...state.byPill,
        [key]: { ...pill, liveText: turnText }
      }
    }
  }

  // commit / discard — hold finished turn until the durable poll catches up.
  const text = (state.turnTextByPill[key] ?? '').trim()
  const { [key]: _removed, ...restTurns } = state.turnTextByPill
  const committed =
    event.type === 'agent_orchestrator_subagent_progress_commit' && text
      ? [...pill.committed.slice(-4), { text, at: event.timestamp }]
      : pill.committed

  return {
    turnTextByPill: restTurns,
    byPill: {
      ...state.byPill,
      [key]: { ...pill, liveText: '', committed }
    }
  }
}
